import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Button, Empty, Modal, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useUser } from "../../../contexts/UserContext/UserContext";
import { useToast } from "../../../contexts/ToastContext";
import apiClient from "../../../helpers/apiClient";
import {
    generateAndStoreSignedAgreement,
    hasRequiredSignatures,
    uploadSignatureCanvas,
} from "../utils/agreementHelpers";
import RentalSignature from "./RentalSignature";

const initialSignatureObjects = {
    signature1: null,
    signature2: null,
    signature3: null,
};

const getAgreementPayload = (reservation) => {
    const pickupDate = reservation.exactPickupTime || reservation.pickupDate;
    const dropoffDate = reservation.exactDropoffTime || reservation.dropoffDate;
    const userId = reservation.userUID || reservation.userId;
    const carSelectedId =
        reservation.carSelected?._id ||
        reservation.carSelected?.id ||
        reservation.carLinked?.[0]?._id ||
        reservation.carLinked?.[0]?.id;
    const daily = reservation.invoice?.charges?.find((charge) => charge.description === "daily");

    return {
        pickupDate,
        dropoffDate,
        userId,
        carSelectedId,
        dailyRate: daily?.rate || 0,
        deposit: reservation.invoice?.deposit || 0,
    };
};

const CheckInAgreementModal = ({
    open,
    confirming = false,
    reservation,
    onCancel,
    onAgreeAndContinue,
    primaryActionLabel,
    signatureLabel,
}) => {
    const { t } = useTranslation(["checkIn"]);
    const toast = useToast();
    const { myCompanyData: companyData } = useUser();
    const multipleSignatures = Boolean(companyData?.preferences?.multipleSignatures);

    const [agreementURL, setAgreementURL] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isStoringAgreement, setIsStoringAgreement] = useState(false);
    const [signatureObjects, setSignatureObjects] = useState(initialSignatureObjects);

    const hasSignature = hasRequiredSignatures(signatureObjects, multipleSignatures);
    const reservationId = String(reservation?.id || reservation?.resId || "").trim();

    useEffect(() => {
        if (!open) {
            setAgreementURL("");
            setHasError(false);
            setIsLoading(false);
            setIsStoringAgreement(false);
            setSignatureObjects(initialSignatureObjects);
            return undefined;
        }

        let objectUrl = "";
        let cancelled = false;

        const generate = async () => {
            const { pickupDate, dropoffDate, userId, carSelectedId, dailyRate, deposit } =
                getAgreementPayload(reservation);

            if (!pickupDate || !dropoffDate || !carSelectedId || !userId) {
                setHasError(true);
                setAgreementURL("");
                return;
            }

            try {
                setIsLoading(true);
                setHasError(false);
                setAgreementURL("");

                const { data } = await apiClient.post(
                    "/api/agreement/generate",
                    {
                        reservation: { pickupDate, dropoffDate },
                        invoice: { daily: dailyRate, deposit },
                        userId,
                        carSelectedId,
                    },
                    { responseType: "blob" },
                );

                if (cancelled) return;

                const blob = new Blob([data], { type: "application/pdf" });
                objectUrl = window.URL.createObjectURL(blob);
                if (cancelled) {
                    window.URL.revokeObjectURL(objectUrl);
                    return;
                }
                setAgreementURL(objectUrl);
            } catch {
                if (!cancelled) {
                    setHasError(true);
                    setAgreementURL("");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        generate();

        return () => {
            cancelled = true;
            if (objectUrl) window.URL.revokeObjectURL(objectUrl);
        };
    }, [open, reservationId, reservation]);

    const setSignature = (key, canvas) => {
        setSignatureObjects((current) => ({
            ...current,
            [key]: canvas,
        }));
    };

    const handleAgreeAndContinue = async () => {
        if (!hasSignature || !reservationId || isStoringAgreement || confirming) return;

        const { pickupDate, dropoffDate, userId, carSelectedId } = getAgreementPayload(reservation);

        if (!pickupDate || !dropoffDate || !carSelectedId || !userId) return;

        setIsStoringAgreement(true);

        try {
            const signature1Url = await uploadSignatureCanvas(signatureObjects.signature1);
            const signature2Url = multipleSignatures
                ? await uploadSignatureCanvas(signatureObjects.signature2)
                : "";
            const signature3Url = multipleSignatures
                ? await uploadSignatureCanvas(signatureObjects.signature3)
                : "";

            await apiClient.patch(`/api/reservation/${reservationId}`, {
                signatures: {
                    signature1: signature1Url,
                    signature2: signature2Url,
                    signature3: signature3Url,
                },
            });

            const agreementDocUrl = await generateAndStoreSignedAgreement(reservationId);

            await onAgreeAndContinue(agreementDocUrl);
        } catch (error) {
            toast.error({
                message: t("agreementFailed"),
                description: error?.response?.data?.message || error?.message,
            });
        } finally {
            setIsStoringAgreement(false);
        }
    };

    const isBusy = confirming || isStoringAgreement;

    return (
        <Modal
            title={t("rentalAgreement")}
            open={open}
            onCancel={onCancel}
            closable={!isBusy}
            maskClosable={false}
            keyboard={!isBusy}
            centered
            width="90%"
            destroyOnClose
            footer={
                <div className="flex justify-center sm:justify-end">
                    <Button
                        type="primary"
                        size="small"
                        loading={isBusy}
                        disabled={isLoading || hasError || !agreementURL || !hasSignature || isBusy}
                        onClick={() => {
                            handleAgreeAndContinue();
                        }}
                        className="!h-8 !rounded-lg !px-4 !text-sm !font-semibold sm:!h-11 sm:!rounded-xl sm:!px-8 sm:!text-base">
                        {primaryActionLabel || t("agreeAndContinue")}
                    </Button>
                </div>
            }>
            <div className="flex w-full flex-col gap-5">
                <div className="flex min-h-[50vh] w-full flex-col sm:min-h-[60vh]">
                    {isLoading ? (
                        <div className="flex flex-grow items-center justify-center">
                            <Spin size="large" />
                        </div>
                    ) : hasError || !agreementURL ? (
                        <div className="flex flex-grow flex-col items-center justify-center gap-2">
                            <Empty description={t("agreementFailed")} />
                        </div>
                    ) : (
                        <object
                            aria-label={t("rentalAgreement")}
                            type="application/pdf"
                            className="h-[50vh] w-full sm:h-[60vh]"
                            data={agreementURL}
                        />
                    )}
                </div>

                <div className="border-t border-[#EEF0F6] pt-4">
                    <p className="fontRegular mb-3 text-sm font-medium text-[#5F6673]">
                        {signatureLabel || t("agreementSignatureLabel")}
                    </p>
                    {multipleSignatures ? (
                        <div className="space-y-4">
                            <RentalSignature
                                onSignatureObject={(canvas) => setSignature("signature1", canvas)}
                                clearLabel={t("clear")}
                            />
                            <RentalSignature
                                onSignatureObject={(canvas) => setSignature("signature2", canvas)}
                                clearLabel={t("clear")}
                            />
                            <RentalSignature
                                onSignatureObject={(canvas) => setSignature("signature3", canvas)}
                                clearLabel={t("clear")}
                            />
                        </div>
                    ) : (
                        <RentalSignature
                            onSignatureObject={(canvas) => setSignature("signature1", canvas)}
                            clearLabel={t("clear")}
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
};

CheckInAgreementModal.propTypes = {
    open: PropTypes.bool,
    confirming: PropTypes.bool,
    reservation: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onAgreeAndContinue: PropTypes.func.isRequired,
    primaryActionLabel: PropTypes.string,
    signatureLabel: PropTypes.string,
};

export default CheckInAgreementModal;
